<template>
  <div class="home mt-2">
    <!-- Encabezado unificado con filtros colapsables -->
    <entity-header
      title="Operadores"
      new-label="Nuevo Operador"
      new-tooltip="Nuevo Operador"
      :has-active-filters="hasActiveFilters"
      @export="getOperadoresExcel"
      @new="showEdit({}, 'add')"
      @search="getOperadoresBusqueda()"
    >
      <template #filters>
        <div class="form-group col mb-2">
          <label class="font-weight-bold small text-muted mb-1" for="numero_corto_select">Por Número</label>
          <input
            type="text"
            class="form-control"
            id="numero_corto_select"
            name="numero_corto_sel"
            placeholder="Filtrar por número..."
            @keyup.enter="getOperadoresBusqueda()"
            v-model="numero_corto_sel"
            aria-label="Numero"
          />
        </div>
        <div class="form-group col-md-3 mb-2">
          <label class="font-weight-bold small text-muted mb-1" for="enabled_select">Por Estado</label>
          <select
            class="form-control"
            v-model="activo_sel"
            name="atributo"
            aria-label="Activo"
            id="enabled_select"
          >
            <option selected :value="null">Todos los estados</option>
            <option :value="0">Inactivo</option>
            <option :value="1">Activo</option>
          </select>
        </div>
      </template>
    </entity-header>

    <div class="alert alert-success" role="alert" v-if="success">
      {{ success }}
    </div>
    <div class="alert alert-danger" role="alert" v-if="error">{{ error }}</div>

    <!-- Tabla de datos estandarizada con acciones -->
    <data-table
      v-if="operadoresBusqueda && operadoresBusqueda.data && operadoresBusqueda.data.length"
      :columns="columns"
      :data="operadoresBusqueda.data"
      :order-by-field="orderByField"
      :order-by-direction="orderByDirection"
      id-key="id_operador"
      @sort="orderByfield"
    >
      <template #cell-integrador="{ item, value }">
        {{ value || getIntegradorName(item.id_integrador) }}
      </template>

      <template #actions="{ item }">
        <table-actions
          :item="item"
          active-key="activo"
          @edit="showEdit(item, 'edit')"
          @toggle="showEdit(item, 'toggle')"
        >
          <template #extra>
            <button
              type="button"
              class="btn btn-primary btn-sm mr-1 pt-0 pb-1"
              data-toggle="tooltip"
              data-placement="top"
              title="Atributos del operador"
              @click="openAtributosModal(item)"
            >
              <small class="bi-bar-chart-steps"></small>
            </button>
          </template>
        </table-actions>
      </template>
    </data-table>

    <!-- Paginador y estado vacío -->
    <Paginador
      v-if="operadoresBusqueda && operadoresBusqueda.pages"
      :pages="operadoresBusqueda.pages"
      @selPage="changePage"
    ></Paginador>

    <empty-state
      :show="Boolean(post && !(operadoresBusqueda && operadoresBusqueda.data && operadoresBusqueda.data.length))"
      message="No se encontraron operadores con el criterio seleccionado."
    />

    <!-- Modal de Atributos del Operador -->
    <crud-modal
      :show="showAtributosModal"
      size="xl"
      :title="atributosModalTitle"
      :show-save="false"
      cancel-text="Cerrar"
      @close="closeAtributosModal"
    >
      <Atributos
        v-if="selectedOperadorAtributos"
        :propOperador="selectedOperadorAtributos.id_operador"
        :propIntegrador="selectedOperadorAtributos.id_integrador"
        :isModal="true"
      />
    </crud-modal>

    <!-- Modal CRUD Estandarizado -->
    <crud-modal
      :show="showModal"
      :title="modalTitle"
      :is-toggle="operation == 'toggle'"
      :show-save="operation == 'edit' || operation == 'add' || operation == 'toggle'"
      :save-text="modalSaveText"
      :save-variant="operation == 'toggle' ? (editData && editData.activo == 1 ? 'warning' : 'success') : 'primary'"
      @close="closeModal"
      @save="onModalSave"
    >
      <template #toggle-info>
        <div
          v-if="operation === 'toggle' && editData && (editData.activo == 1 || editData.activo == '1')"
          class="alert alert-info d-flex align-items-center mb-3"
          role="alert"
        >
          <i class="bi bi-info-circle-fill mr-2" style="font-size: 1.25rem;"></i>
          <div>
            <strong>Información:</strong> Esto implica que no se podrán dar nuevas altas sobre los servicios asociados, esto no afecta a las solicitudes de bajas.
          </div>
        </div>
      </template>

      <template #toggle-message>
        <div v-if="operation === 'toggle' && editData && (editData.activo == 1 || editData.activo == '1')">
          <strong>Advertencia:</strong> Al desactivar este operador se desactivarán automáticamente sus servicios asociados.
        </div>
      </template>

      <form
        v-if="
          editData &&
          (operation == 'delete' ||
          operation == 'edit' ||
          operation == 'add')
        "
      >
        <div v-if="operation == 'add' && isTodosIntegrador" class="form-group">
          <label>Integrador</label>
          <select
            class="form-control"
            name="id_integrador"
            :class="{ 'is-invalid': hasFieldError('id_integrador') }"
            v-model="editData.id_integrador"
            required
          >
            <option :value="null">Seleccione un integrador...</option>
            <option
              v-for="item in integradoresList"
              :key="item.id_integrador || item.id"
              :value="item.id_integrador || item.id"
            >
              {{ item.integrador }}
            </option>
          </select>
          <small class="form-text text-muted">Seleccione el integrador para este operador.</small>
        </div>
        <div v-else-if="operation == 'edit' || operation == 'delete'" class="form-group">
          <label>Integrador</label>
          <input
            type="text"
            class="form-control"
            :value="editData.integrador || getIntegradorName(editData.id_integrador)"
            disabled
            readonly
          />
          <small class="form-text text-muted">Integrador al que pertenece el operador (no editable).</small>
        </div>
        <div v-else-if="operation == 'add'" class="form-group">
          <label>Integrador</label>
          <input
            type="text"
            class="form-control"
            :value="getIntegradorName(id_integrador_sel)"
            disabled
            readonly
          />
          <small class="form-text text-muted">Integrador seleccionado para el nuevo operador (no editable).</small>
        </div>

        <TextForm
          title="Número"
          name="numero_corto"
          placeholder="Ej: 2025"
          hint="Número corto visible para el usuario (dígitos numéricos)."
          :value="editData.numero_corto || editData.numero"
          @value="editData.numero_corto = $event; editData.numero = $event"
          :errors="getError('numero_corto') || getError('numero')"
        />
      </form>

      <div
        v-if="getErrorMessage"
        class="alert alert-danger my-2"
        role="alert"
      >
        <i class="bi bi-exclamation-triangle me-1"></i>
        {{ getErrorMessage }}
      </div>
    </crud-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { entityCrudMixin } from "@/mixins/entityCrudMixin";
import { startAsyncExport } from "@/utils/exportHelper";
import EntityHeader from "./utiles/EntityHeader.vue";
import DataTable from "./utiles/DataTable.vue";
import TableActions from "./utiles/TableActions.vue";
import EmptyState from "./utiles/EmptyState.vue";
import Paginador from "./Paginador.vue";
import CrudModal from "./utiles/CrudModal.vue";
import TextForm from "./forms/TextForm.vue";
import Atributos from "./Atributos.vue";

export default {
  name: "Operadores",
  mixins: [entityCrudMixin],

  components: {
    EntityHeader,
    DataTable,
    TableActions,
    EmptyState,
    Paginador,
    CrudModal,
    TextForm,
    Atributos,
  },

  data() {
    return {
      columns: [
        { label: "Id", field: "id_operador", width: "60" },
        { label: "Integrador", field: "integrador" },
        { label: "Numero", field: "numero_corto" },
        { label: "Estado", field: "activo", type: "badge", width: "110" },
      ],
      showAtributosModal: false,
      selectedOperadorAtributos: null,
      success: null,
      error: null,
      operadoresBusqueda: null,
      id_integrador_sel: localStorage.getItem("integrador") || null,
      numero_corto_sel: null,
      activo_sel: null,
      valor: null,
      id_operador: null,
    };
  },

  created() {
    this.orderByField = "id_operador";
    this.getOperadoresBusqueda();
  },

  computed: {
    ...mapGetters("auth", ["user"]),
    ...mapGetters("integrador", ["integradores"]),

    modalTitle() {
      if (!this.editData) return "";
      if (this.operation === "toggle") {
        return this.editData.activo == 1
          ? "Suspender Operador"
          : "Reactivar Operador";
      }
      if (this.operation === "edit") {
        return `Editar Operador ${this.editData.id_operador || ""}`;
      }
      if (this.operation === "add") {
        return "Nuevo Operador";
      }
      return "";
    },

    modalSaveText() {
      if (this.operation === "toggle") {
        return this.editData && this.editData.activo == 1 ? "Suspender" : "Reactivar";
      }
      return "Guardar";
    },

    atributosModalTitle() {
      if (!this.selectedOperadorAtributos) return "Atributos del Operador";
      const op = this.selectedOperadorAtributos;
      const num = op.numero_corto ? `Número: ${op.numero_corto}` : `ID: ${op.id_operador}`;
      return `Atributos del Operador: ${num}`;
    },

    getErrorMessage() {
      if (!this.errors) return null;
      if (typeof this.errors === "string") return this.errors;
      return this.errors.error || this.errors.message || (this.errors.errors ? null : JSON.stringify(this.errors));
    },

    hasActiveFilters() {
      return (
        Boolean(this.numero_corto_sel) ||
        (this.activo_sel !== null && this.activo_sel !== undefined)
      );
    },

    isTodosIntegrador() {
      return !this.id_integrador_sel || this.id_integrador_sel === "todos";
    },

    integradoresList() {
      let list = [];
      if (this.integradores && this.integradores.data && this.integradores.data.length > 0) {
        list = [...this.integradores.data];
      } else if (this.user && this.user.integradores && Array.isArray(this.user.integradores)) {
        list = [...this.user.integradores];
      }
      return list.sort((a, b) =>
        String(a.integrador || "").localeCompare(String(b.integrador || ""), undefined, { sensitivity: "base" })
      );
    },
  },

  watch: {
    "$store.state.integrador.integrador"(newVal) {
      this.id_integrador_sel = newVal;
      this.page = 1;
      this.getOperadoresBusqueda();
    },
  },

  methods: {
    showEdit(item = {}, operation = "edit") {
      this.errors = null;
      this.operation = operation;
      this.editData = Object.assign({}, item);
      if (operation === "add") {
        this.editData.id_integrador = this.isTodosIntegrador
          ? null
          : parseInt(this.id_integrador_sel);
      }
      this.showModal = true;
    },
    getIntegradorName(id) {
      if (!id) return "";
      const list = this.integradores && this.integradores.data ? this.integradores.data : (Array.isArray(this.integradores) ? this.integradores : []);
      const found = list.find((i) => String(i.id_integrador || i.id) === String(id));
      return found ? found.integrador : id;
    },
    buscar(hideResult = false) {
      this.getOperadoresBusqueda(hideResult);
    },

    onModalSave() {
      if (this.operation === "toggle") return this.toggleStatus();
      if (this.operation === "edit") return this.editOperador();
      if (this.operation === "add") return this.agregarOperador();
    },

    openAtributosModal(operador) {
      this.selectedOperadorAtributos = Object.assign({}, operador);
      this.showAtributosModal = true;
    },

    closeAtributosModal() {
      this.showAtributosModal = false;
      this.selectedOperadorAtributos = null;
    },

    getOperadoresBusqueda(hideResult = true) {
      if (hideResult) this.operadoresBusqueda = null;
      this.post = false;
      this.$store.commit("setLoading", true);
      return axios
        .post(process.env.VUE_APP_API_URL + "operador/busqueda", {
          page: this.page,
          id_integrador: this.id_integrador_sel,
          numero_corto: this.numero_corto_sel,
          activo: this.activo_sel,
          order_by: { field: this.orderByField, order: this.orderByDirection },
        })
        .then((response) => {
          this.operadoresBusqueda = response.data;
        })
        .catch((e) => {
          if (!this.errors) this.errors = [];
          if (Array.isArray(this.errors)) this.errors.push(e);
        })
        .finally(() => {
          this.$store.commit("setLoading", false);
          this.post = true;
        });
    },

    deleteOperador() {
      this.errors = null;
      this.$store.commit("setLoading", true);
      axios
        .post(process.env.VUE_APP_API_URL + "operador/delete", {
          id_operador: this.editData.id_operador,
          activo: this.editData.activo == 1 ? 0 : 1,
        })
        .then((response) => {
          if (
            response &&
            response.data &&
            response.data.errno &&
            response.data.errno > 0
          ) {
            this.errors = response.data.error;
          } else {
            this.closeModal();
            Swal.fire({
              title: "Bien!",
              text: "Operador borrado correctamente",
              icon: "success",
              confirmButtonText: "Aceptar",
            });
          }
        })
        .catch((e) => {
          if (e.response && e.response.data && e.response.data.error) {
            this.errors = e.response.data.error;
          } else {
            Swal.fire({
              title: "Error!",
              text: "Ha ocurrido un error al intentar borrar operador",
              icon: "error",
              confirmButtonText: "Aceptar",
            });
          }
        })
        .finally(() => {
          this.$store.commit("setLoading", false);
          if (this.operation == null) this.getOperadoresBusqueda();
        });
    },

    editOperador() {
      this.errors = null;
      this.$store.commit("setLoading", true);
      axios
        .post(process.env.VUE_APP_API_URL + "operador/update", this.editData)
        .then((response) => {
          if (
            response &&
            response.data &&
            response.data.errno &&
            response.data.errno > 0
          ) {
            this.errors = response.data;
          } else {
            this.closeModal();
            Swal.fire({
              title: "Bien!",
              text: "Operador modificado correctamente",
              icon: "success",
              confirmButtonText: "Aceptar",
            });
          }
        })
        .catch((e) => {
          if (e.response && e.response.data && e.response.data.error) {
            this.errors = e.response.data;
          } else {
            Swal.fire({
              title: "Error!",
              text: "Ha ocurrido un error al intentar modificar operador",
              icon: "error",
              confirmButtonText: "Aceptar",
            });
          }
        })
        .finally(() => {
          this.$store.commit("setLoading", false);
          if (this.operation == null) this.getOperadoresBusqueda();
        });
    },

    agregarOperador() {
      this.errors = null;
      if (!this.editData.id_integrador || this.editData.id_integrador === "todos") {
        if (!this.isTodosIntegrador) {
          this.editData.id_integrador = parseInt(this.id_integrador_sel);
        } else {
          this.errors = "Debe seleccionar un integrador para el nuevo operador";
          return;
        }
      }
      this.$store.commit("setLoading", true);
      axios
        .post(process.env.VUE_APP_API_URL + "operador/new", this.editData)
        .then((response) => {
          if (
            response &&
            response.data &&
            response.data.errno &&
            response.data.errno > 0
          ) {
            this.errors = response.data;
          } else {
            const createdOp =
              response.data && response.data.data
                ? Object.assign({}, response.data.data)
                : {
                    id_operador:
                      response.data && (response.data.id_operador || response.data.id),
                    numero_corto: this.editData.numero_corto || this.editData.numero,
                    activo: this.editData.activo !== undefined ? this.editData.activo : 1,
                  };
            if (!createdOp.id_operador && response.data && response.data.id_operador) {
              createdOp.id_operador = response.data.id_operador;
            }
            if (!createdOp.numero_corto) {
              createdOp.numero_corto = this.editData.numero_corto || this.editData.numero;
            }

            this.closeModal();
            this.openAtributosModal(createdOp);
          }
        })
        .catch((e) => {
          if (e.response && e.response.data && e.response.data.error) {
            this.errors = e.response.data;
          } else {
            Swal.fire({
              title: "Error!",
              text: "Ha ocurrido un error al intentar guardar",
              icon: "error",
              confirmButtonText: "Aceptar",
            });
          }
        })
        .finally(() => {
          this.$store.commit("setLoading", false);
          if (this.operation == null) this.getOperadoresBusqueda();
        });
    },

    getOperadoresExcel() {
      startAsyncExport(
        "operador",
        {
          id_integrador: this.id_integrador_sel,
          order_by: {
            field: this.orderByField,
            order: this.orderByDirection,
          },
        },
        "Operadores"
      );
    },

    toggleStatus() {
      this.errors = null;
      this.$store.commit("setLoading", true);
      const targetActivo = 1 - Number(this.editData.activo || 0);
      const payload = {
        ...this.editData,
        activo: targetActivo,
      };
      axios
        .post(process.env.VUE_APP_API_URL + "operador/update", payload)
        .then((response) => {
          if (
            response &&
            response.data &&
            response.data.errno &&
            response.data.errno > 0
          ) {
            this.errors = response.data.error;
          } else {
            this.closeModal();
            Swal.fire({
              title: "Bien!",
              text: "Operador actualizado correctamente",
              icon: "success",
              confirmButtonText: "Aceptar",
            });
          }
        })
        .catch((e) => {
          if (e.response && e.response.data && e.response.data.error) {
            this.errors = e.response.data.error;
            Swal.fire({
              title: "Error!",
              text: e.response.data.error,
              icon: "error",
              confirmButtonText: "Aceptar",
            });
          } else {
            Swal.fire({
              title: "Error!",
              text: "Ha ocurrido un error al intentar guardar",
              icon: "error",
              confirmButtonText: "Aceptar",
            });
          }
        })
        .finally(() => {
          this.$store.commit("setLoading", false);
          if (this.operation == null) this.getOperadoresBusqueda();
        });
    },

    getError(name) {
      if (name) {
        return this.errors && this.errors.errors && this.errors.errors[name]
          ? this.errors.errors[name]
          : null;
      }
      if (!this.errors) return null;
      if (typeof this.errors === "string") return this.errors;
      return this.errors.error || this.errors.message || JSON.stringify(this.errors);
    },
  },
};
</script>

<style scoped></style>
